import { ApolloCustomerErrorAlert } from '@graphcommerce/magento-customer/components/ApolloCustomerError/ApolloCustomerErrorAlert'
import {
  UpdateCustomerEmailDocument,
  UpdateCustomerEmailMutation,
  UpdateCustomerEmailMutationVariables,
} from '@graphcommerce/magento-customer/components/UpdateCustomerEmailForm/UpdateCustomerEmail.gql'
import { Form, MessageSnackbar } from '@graphcommerce/next-ui'
import { emailPattern, useFormGqlMutation } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Paper } from '@mui/material'
import { StandardButton } from '../../Button/ButtonStyles'
import { StandardTextField } from '../../TextInput/TextFieldStyles'

type UpdateCustomerEmailFormProps = {
  email: string
}

export function UpdateCustomerEmailForm(props: UpdateCustomerEmailFormProps) {
  const { email } = props

  const form = useFormGqlMutation<
    UpdateCustomerEmailMutation,
    UpdateCustomerEmailMutationVariables & {
      currentEmail?: string
      confirmEmail?: string
    }
  >(UpdateCustomerEmailDocument)

  const { handleSubmit, error, required, formState, watch, register, reset } = form
  const submit = handleSubmit(() => {
    reset()
  })
  const watchNewEmail = watch('email')

  return (
    <Form onSubmit={submit} noValidate className='mb-10 mt-0 py-0'>
      <Paper elevation={3} className='md:px-15 p-10 md:py-8'>
        <span className='Type-XXL-Regular w-full'>Email address</span>
        <hr className='mb-7 mt-2 text-20-grey' />
        <div className='flex flex-col items-center justify-center gap-x-5 gap-y-5 md:items-start'>
          <StandardTextField
            id='current-email'
            label='Current email'
            variant='outlined'
            color='primary'
            autoComplete='currentEmail'
            error={formState.isSubmitted && !!formState.errors.currentEmail}
            required
            value={email}
            {...register('currentEmail', {
              required: true,
              pattern: { value: emailPattern, message: '' },
            })}
            className='min-w-[260px] outline-0'
            size='small'
            sx={{
              width: '260px',
              minWidth: '260px',
            }}
          />
          <div className='space-between flex flex-col gap-y-5 md:flex-row md:gap-x-[40px]'>
            <StandardTextField
              id='email'
              label={<Trans id='New email' />}
              variant='outlined'
              color='primary'
              autoComplete='email'
              error={formState.isSubmitted && !!formState.errors.email}
              required={required.email}
              {...register('email', {
                required: true,
                pattern: { value: emailPattern, message: '' },
              })}
              className='min-w-[260px] outline-0'
              size='small'
              sx={{
                width: '260px',
                minWidth: '260px',
              }}
            />
            <StandardTextField
              id='confirm-email'
              label={<Trans id='Confirm new email' />}
              variant='outlined'
              color='primary'
              autoComplete='confirmEmail'
              error={formState.isSubmitted && !!formState.errors.confirmEmail}
              required
              {...register('confirmEmail', {
                required: true,
                validate: (value) =>
                  value === watchNewEmail || i18n._(/* i18n */ "Emails don't match"),
              })}
              className='min-w-[260px] outline-0'
              size='small'
              sx={{
                width: '260px',
                minWidth: '260px',
              }}
            />
          </div>
          <StandardTextField
            variant='outlined'
            color='primary'
            label={<Trans id='Password' />}
            type='password'
            error={!!formState.errors.password}
            autoComplete='password'
            required={required.password}
            {...register('password', {
              required: required.password,
            })}
            disabled={formState.isSubmitting}
            className='min-w-[260px] outline-0'
            size='small'
            sx={{
              width: '260px',
              minWidth: '260px',
            }}
          />
        </div>
        <div className='mt-12 flex w-full flex-col items-center justify-center gap-[30px] md:flex-row md:justify-start'>
          <StandardButton
            type='submit'
            className='w-full max-w-[284px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            loading={formState.isSubmitting}
          >
            <Trans id='Save changes' />
          </StandardButton>
        </div>
        <ApolloCustomerErrorAlert error={error} />
      </Paper>
      <MessageSnackbar sticky open={formState.isSubmitSuccessful && !error}>
        <Trans id='Successfully updated email' />
      </MessageSnackbar>
    </Form>
  )
}
